export interface TurnSegment {
  turnNumberID: number;
  timestamps: number[];
  angleData: number[];
}

/**
 * Split timestamp + angle arrays into turns.
 * Each time angle crosses a multiple of 360, we start a new segment.
 *
 * @param timestamps - array of timestamps
 * @param angles - array of angles (continuous, increasing over time)
 * @returns array of turn segments
 */
export function splitTurns(timestamps: number[], angles: number[]): TurnSegment[] {
  if (timestamps.length !== angles.length) {
    throw new Error('timestamps and angles must be the same length');
  }
  if (angles.length === 0) return [];

  const segments: TurnSegment[] = [];

  let currentTurn = 0;
  let currentTimestamps: number[] = [];
  let currentAngles: number[] = [];

  const base = Math.floor(angles[0] / 360); // handle if we start mid-turn
  currentTurn = base;

  for (let i = 0; i < angles.length; i++) {
    const normalizedTurn = Math.floor(angles[i] / 360);

    // If we moved to the next turn, close the current segment
    if (normalizedTurn !== currentTurn) {
      segments.push({
        turnNumberID: currentTurn - base,
        timestamps: currentTimestamps,
        angleData: currentAngles,
      });

      // Reset arrays for next turn
      currentTimestamps = [];
      currentAngles = [];
      currentTurn = normalizedTurn;
    }

    currentTimestamps.push(timestamps[i]);
    currentAngles.push(angles[i] % 360); // wrap angle inside [0,360)
  }

  // Push the last segment
  if (currentTimestamps.length > 0 && currentAngles.length > 3) {
    segments.push({
      turnNumberID: currentTurn - base,
      timestamps: currentTimestamps,
      angleData: currentAngles,
    });
  }

  return segments;
}
